import { Trash2, Plus } from "lucide-react";

export const DAYS_ORDER = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const DAY_LABELS: Record<string, string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];
export const DoctorSessionTimingSelection = ({
  consultationFee,
  enabledDays,
  timeFrames,
  addingSlots,
  setConsultationFee,
  setAddingSlots,
  toggleDay,
  handleAddSlotClick,
  handleSaveSlot,
  handleRemoveSlot,
  handleTimeSlotChange,
  handleApplySameAs,
  isGenerating,
}: any) => {
  return (
    <div className="space-y-10 py-2">
      {/* Consultation Fee */}
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Consultation Fee
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
            ₹
          </span>
          <input
            type="number"
            value={consultationFee}
            onChange={(e) => setConsultationFee(parseInt(e.target.value) || 0)}
            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Enter consultation fee"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleApplySameAs}
          className="text-sm text-green-600 hover:text-green-700 font-medium underline"
          type="button"
        >
          Same for all
        </button>
      </div>

      {/* Days and Slots */}
      {DAYS_ORDER.map((day) => {
        const dayEnabled = enabledDays[day];
        const slots = timeFrames.filter((s: any) => s.day_of_week === day);
        const addingSlot = addingSlots[day];

        return (
          <div key={day} className="space-y-3">
            <div className="flex items-start gap-4">
              {/* Day toggle */}
              <button
                disabled={isGenerating}
                className={`px-3 py-1 rounded text-sm font-medium min-w-[50px] transition-colors ${
                  dayEnabled
                    ? "bg-green-600 text-white"
                    : "bg-gray-300 text-gray-600"
                }`}
                onClick={() => toggleDay(day)}
              >
                {DAY_LABELS[day]}
              </button>

              {/* Time Slots */}
              <div className="flex-1 space-y-2">
                {dayEnabled ? (
                  <>
                    {/* Existing slots */}
                    {slots.map((slot: any) => (
                      <div key={slot.id} className="flex items-center gap-2">
                        <input
                          type="time"
                          disabled={isGenerating}
                          value={slot.start_time}
                          onChange={(e) =>
                            handleTimeSlotChange(
                              slot.id,
                              "start_time",
                              e.target.value,
                            )
                          }
                          className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50"
                        />
                        <span className="text-gray-400">→</span>
                        <input
                          type="time"
                          disabled={isGenerating}
                          value={slot.end_time}
                          onChange={(e) =>
                            handleTimeSlotChange(
                              slot.id,
                              "end_time",
                              e.target.value,
                            )
                          }
                          className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50"
                        />
                        <span className="text-sm text-gray-600">
                          {slot.slot_duration}m
                        </span>
                        <button
                          disabled={isGenerating}
                          onClick={() => handleRemoveSlot(slot.id)}
                          className="p-1 text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}

                    {/* Add Slot Form */}
                    {addingSlot && (
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                        <input
                          type="time"
                          disabled={isGenerating}
                          value={addingSlot.start_time}
                          onChange={(e) =>
                            setAddingSlots((prev: any) => ({
                              ...prev,
                              [day]: {
                                ...addingSlot,
                                start_time: e.target.value,
                              },
                            }))
                          }
                          className="flex-1 px-3 py-2 border border-green-300 rounded text-sm"
                        />
                        <span className="text-gray-400">→</span>
                        <input
                          type="time"
                          disabled={isGenerating}
                          value={addingSlot.end_time}
                          onChange={(e) =>
                            setAddingSlots((prev: any) => ({
                              ...prev,
                              [day]: {
                                ...addingSlot,
                                end_time: e.target.value,
                              },
                            }))
                          }
                          className="flex-1 px-3 py-2 border border-green-300 rounded text-sm"
                        />
                        <select
                          value={addingSlot.slot_duration}
                          disabled={isGenerating}
                          onChange={(e) =>
                            setAddingSlots((prev: any) => ({
                              ...prev,
                              [day]: {
                                ...addingSlot,
                                slot_duration: parseInt(e.target.value),
                              },
                            }))
                          }
                          className="px-2 py-2 border border-green-300 rounded text-sm"
                        >
                          {DURATION_OPTIONS.map((d) => (
                            <option key={d} value={d}>
                              {d}m
                            </option>
                          ))}
                        </select>
                        <button
                          disabled={isGenerating}
                          onClick={() => handleSaveSlot(day)}
                          className="px-3 py-2 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          disabled={isGenerating}
                          onClick={() =>
                            setAddingSlots((prev: any) => ({
                              ...prev,
                              [day]: null,
                            }))
                          }
                          className="px-3 py-2 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {!addingSlot && (
                      <button
                        disabled={isGenerating}
                        onClick={() => handleAddSlotClick(day)}
                        className="w-full py-2 bg-green-100 text-green-700 rounded border border-green-200 hover:bg-green-150 text-sm font-medium flex items-center justify-center gap-1"
                      >
                        <Plus size={16} /> Add Slot
                      </button>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-500 italic py-2">
                    Disabled — click day to enable
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
